import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Archive, ArchiveRestore, ArrowUpRight, Check, Clipboard, FileText, History, Search, Sparkles, Trash2, X } from 'lucide-react'
import { AiFeedbackControl, ReportProblemButton } from '../../../../analytics'
import { CFO_DRILLDOWN_MANIFEST } from '../../../../ai/cfoDrilldownManifest'
import { isOpenEndedAskEnabled } from '../../../../ai/openEndedAsk'
import type { AppWorkerClient } from '../../../../data/AppWorkerClient'
import type { ServerApiClient } from '../../../../data/ServerApiClient'
import { useCfoCopilot } from '../useCfoCopilot'
import { buildCfoAnalysisProgressMessages } from './progressMessages'
import { CfoAnalysisChart, CfoAnalysisStream } from './CfoAnalysisStream'
import { fallbackChipsForSession } from './cfoDrilldownPrompts'
import { CfoInstantCardView, compactInstantCardForAgent, comparisonShortLabel } from './instantCards'
import {
  applyCfoNextLevelBreakdown,
  fetchCfoNextLevelBreakdown,
  shouldFetchCfoNextLevelBreakdown,
  type CfoNextLevelBreakdown,
} from './nextLevelDrivers'
import { normalizeCfoDrilldownComparisonKind } from '../../../../ai/cfoDrilldownRecipes'
import { CfoShellSelectChip } from '../CfoPageShell'
import { RATIO_WINDOW_OPTIONS, RatioDetailCard, type RatioWindow } from '../ratios/RatioDetailCard'
import { getCachedQuickBooksRatios } from '../cfoRatiosCache'
import { groundingFromRatioCard } from '../cfoRatioHealthVisual'
import { buildCfoRoutePath, CFO_ROUTE_FOCUS_METRIC_PARAM, parseCfoRouteState, type CfoPageSlug } from '../cfoRoutes'
import { buildCfoAnalysisSessionHtml } from './CfoAnalysisSessionHtml'
import type { CfoAnalysisActiveSession } from './CfoAnalysisProvider'
import type { AiAgentContinueResponse, AiChatMessage, RatioCard } from '../../../../data/ServerApiClient'
import type { CfoAiContextPacket, CfoInstantCardAction, CfoInstantCardChangeExplanation, CfoInstantCardEnrichment, CfoPersistedSession, CfoReportAnalysisItem } from './types'
import './CfoAnalysisModal.css'

const trimStr = (value: unknown): string => String(value ?? '').trim()
const isRecord = (value: unknown): value is Record<string, any> => Boolean(value && typeof value === 'object' && !Array.isArray(value))
const sourceComponentForSession = (session: CfoAnalysisActiveSession): string => trimStr(session.context.sourceComponent)
const EVIDENCE_CLICK_CONTEXT_BUDGET_BYTES = 8192
const CONTEXT_INSIGHT_CONTEXT_BUDGET_BYTES = 32768
const ENRICHMENT_SLOW_MS = 1500

/** Comparison windows the next-level breakdown can re-run at — the same set
 *  the segment-CM panel offers on the Financials page. */
const NEXT_LEVEL_COMPARISON_OPTIONS = [
  { id: 'mom', label: 'vs prior month' },
  { id: 'qoq', label: 'vs prior quarter' },
  { id: 'yoy', label: 'vs prior year' },
  { id: 'ytd', label: 'YTD vs prior YTD' },
  { id: 'l3m_vs_prior3m', label: 'Last 3mo vs prior 3mo' },
  { id: 'l12m_vs_prior12m', label: 'Last 12mo vs prior 12mo' },
]

/**
 * Navigate to another CFO page, carrying identifying params. Returns false when
 * the current location is not a parseable CFO route (e.g. embedded/dev shells),
 * in which case callers fall back to prompt-only behavior.
 */
const assignCfoPageUrl = (page: CfoPageSlug, params: Record<string, string | null | undefined>): boolean => {
  const route = parseCfoRouteState(window.location)
  if (!route) return false
  const next = new URL(
    buildCfoRoutePath({ siteSlug: route.siteSlug, workbookId: route.workbookId, page }),
    window.location.origin,
  )
  for (const [key, value] of Object.entries(params)) {
    const clean = trimStr(value)
    if (clean) next.searchParams.set(key, clean)
  }
  window.location.assign(next.toString())
  return true
}

const cfoPageForActionTarget = (action: CfoInstantCardAction): CfoPageSlug | null => {
  const surface = trimStr(action.target?.surface || '').toLowerCase()
  const kind = trimStr(action.kind).toLowerCase()
  if (kind === 'transactions') {
    if (surface.includes('ar')) return 'ar'
    return 'gl-detail'
  }
  if (surface.includes('financial')) return 'financials'
  if (surface.includes('ratio')) return 'ratios'
  if (surface.includes('ar') || surface.includes('receivable')) return 'ar'
  if (surface.includes('forecast') || surface.includes('cash')) return 'forecast'
  if (surface.includes('scenario')) return 'scenarios'
  if (surface.includes('valuation')) return 'valuation'
  if (surface.includes('presentation') || surface.includes('report')) return 'presentations'
  if (surface.includes('gl')) return 'gl-detail'
  return null
}

const instantCardEnrichmentPrompt = (session: CfoAnalysisActiveSession): string =>
  [
    `Find fast next-level exploration options for ${session.context.metricLabel || session.instantCard?.title || 'this CFO metric'}.`,
    'Use only bounded fast-lane tools and the compact instant_card context.',
    'Return specific next steps a CFO can click, such as contribution margin, service lines, child accounts, customers, vendors, or GL detail when available.',
  ].join(' ')

const factsFromEnrichmentResponse = (
  response: AiAgentContinueResponse | null | undefined,
  latencyMs: number | null,
): CfoInstantCardEnrichment['summaryFacts'] => {
  const facts: CfoInstantCardEnrichment['summaryFacts'] = []
  const toolNames = Array.isArray(response?.tool_calls)
    ? response!.tool_calls.map((call) => trimStr(call.name)).filter(Boolean)
    : []
  if (latencyMs != null) facts.push({ label: 'Prepared in', value: `${latencyMs}ms`, tone: latencyMs > ENRICHMENT_SLOW_MS ? 'watch' : 'good' })
  facts.push({ label: 'Fast-lane tools', value: toolNames.length ? String(toolNames.length) : '0', tone: toolNames.length ? 'neutral' : 'good' })
  const headline = trimStr(response?.answer?.headline)
  if (headline) facts.push({ label: 'Next insight', value: headline })
  return facts.slice(0, 4)
}

const optionsFromEnrichmentResponse = (
  response: AiAgentContinueResponse | null | undefined,
  session: CfoAnalysisActiveSession,
): CfoInstantCardAction[] => {
  const fromAgent = (response?.followups || [])
    .map((followup) => ({
      kind: 'ask',
      label: trimStr(followup.label),
      prompt: trimStr(followup.prompt),
      target: {
        surface: session.context.activeSection,
        metricId: session.context.metricId || null,
      },
    }))
    .filter((action) => action.label && action.prompt)
  const fromCard = [...(session.instantCard?.nextSteps || []), ...(session.instantCard?.actions || [])]
    .map((action) => ({
      ...action,
      label: trimStr(action.label),
      prompt: trimStr(action.prompt),
      kind: action.kind || 'ask',
      target: action.target || { surface: session.context.activeSection, metricId: session.context.metricId || null },
    }))
    .filter((action) => action.label && action.prompt)
  const deduped = new Map<string, CfoInstantCardAction>()
  for (const action of [...fromAgent, ...fromCard]) {
    const key = `${action.kind || 'ask'}:${action.label.toLowerCase()}`
    if (!deduped.has(key)) deduped.set(key, action)
  }
  return Array.from(deduped.values()).slice(0, 5)
}

const mapChangeExplanation = (item: any): CfoInstantCardChangeExplanation | null => {
  const label = trimStr(item?.label)
  const headline = trimStr(item?.headline)
  if (!label || !headline) return null
  const drivers = Array.isArray(item?.drivers)
    ? item.drivers
      .map((driver: any, index: number) => ({
        id: trimStr(driver?.id) || `${trimStr(item?.periodKind || item?.period_kind) || label}:driver:${index}`,
        label: trimStr(driver?.label),
        value: trimStr(driver?.value),
        rawValue: Number.isFinite(Number(driver?.rawValue ?? driver?.raw_value)) ? Number(driver?.rawValue ?? driver?.raw_value) : null,
        shareOfChange: Number.isFinite(Number(driver?.shareOfChange ?? driver?.share_of_change)) ? Number(driver?.shareOfChange ?? driver?.share_of_change) : null,
        direction: (['up', 'down', 'flat'].includes(trimStr(driver?.direction)) ? trimStr(driver?.direction) : 'flat') as 'up' | 'down' | 'flat',
        category: trimStr(driver?.category) || null,
        sourceSurface: trimStr(driver?.sourceSurface || driver?.source_surface) || null,
        target: isRecord(driver?.target) ? driver.target : null,
      }))
      .filter((driver: any) => driver.label && driver.value)
      .slice(0, 6)
    : []
  return {
    periodKind: trimStr(item?.periodKind || item?.period_kind) || 'current_month',
    label,
    status: (['ready', 'partial', 'unsupported', 'stale'].includes(trimStr(item?.status)) ? trimStr(item?.status) : 'partial') as CfoInstantCardChangeExplanation['status'],
    current: trimStr(item?.current) || '-',
    prior: trimStr(item?.prior) || '-',
    change: trimStr(item?.change) || '-',
    headline,
    drivers,
    confidence: (['high', 'medium', 'low'].includes(trimStr(item?.confidence)) ? trimStr(item?.confidence) : 'medium') as CfoInstantCardChangeExplanation['confidence'],
    caveats: Array.isArray(item?.caveats) ? item.caveats.map(trimStr).filter(Boolean).slice(0, 3) : undefined,
    recommendedAction: trimStr(item?.recommendedAction || item?.recommended_action) || undefined,
  }
}

export const mapInstantCardEnrichmentPacket = (
  rowOrResponse: unknown,
  cacheHit: boolean,
): CfoInstantCardEnrichment | null => {
  const row = isRecord(rowOrResponse)
    ? (isRecord((rowOrResponse as any).packet) ? (rowOrResponse as any).packet : rowOrResponse)
    : null
  if (!row) return null
  const payload = isRecord(row.payload_json)
    ? row.payload_json
    : isRecord(row.payload)
      ? row.payload
      : row
  const status = trimStr(payload.status || row.status || (cacheHit ? 'ready' : ''))
  const cardKey = trimStr(payload.cardKey || payload.card_key || row.card_key || row.cardKey)
  if (!cardKey || !status) return null
  const summaryFactsRaw = Array.isArray(payload.summaryFacts)
    ? payload.summaryFacts
    : Array.isArray(payload.summary_facts)
      ? payload.summary_facts
      : Array.isArray(row.summary_facts_json)
        ? row.summary_facts_json
        : []
  const suggestedOptionsRaw = Array.isArray(payload.suggestedOptions)
    ? payload.suggestedOptions
    : Array.isArray(payload.suggested_options)
      ? payload.suggested_options
      : Array.isArray(row.suggested_options_json)
        ? row.suggested_options_json
        : []
  const changeExplanationsRaw = Array.isArray(payload.changeExplanations)
    ? payload.changeExplanations
    : Array.isArray(payload.change_explanations)
      ? payload.change_explanations
      : Array.isArray(row.change_explanations_json)
        ? row.change_explanations_json
        : []
  const statusForUi: CfoInstantCardEnrichment['status'] =
    status === 'ready'
      ? 'ready'
      : status === 'unsupported'
        ? 'unsupported'
        : status === 'preparing' || status === 'not_ready'
          ? 'preparing'
          : status === 'error'
            ? 'error'
            : 'loading'
  return {
    cardKey,
    freshnessToken: trimStr(payload.freshnessToken || payload.freshness_token || row.freshness_token) || null,
    status: statusForUi,
    suggestedOptions: suggestedOptionsRaw
      .map((action: any) => ({
        ...action,
        label: trimStr(action?.label),
        prompt: trimStr(action?.prompt),
        kind: trimStr(action?.kind) || 'ask',
        target: isRecord(action?.target) ? action.target : null,
      }))
      .filter((action: CfoInstantCardAction) => action.label && action.prompt)
      .slice(0, 5),
    summaryFacts: summaryFactsRaw
      .map((fact: any) => ({
        label: trimStr(fact?.label),
        value: trimStr(fact?.value),
        tone: (fact?.tone === 'good' || fact?.tone === 'bad' || fact?.tone === 'watch' || fact?.tone === 'neutral')
          ? fact.tone
          : null,
      }))
      .filter((fact: any) => fact.label && fact.value)
      .slice(0, 4),
    changeExplanations: changeExplanationsRaw
      .map(mapChangeExplanation)
      .filter((item: CfoInstantCardChangeExplanation | null): item is CfoInstantCardChangeExplanation => item != null)
      .slice(0, 4),
    latencyMs: Number.isFinite(Number(payload.latencyMs ?? payload.latency_ms ?? row.build_latency_ms))
      ? Number(payload.latencyMs ?? payload.latency_ms ?? row.build_latency_ms)
      : null,
    source: trimStr(payload.source || row.source) || 'persisted_packet',
    toolNames: Array.isArray(payload.toolNames) ? payload.toolNames.map(trimStr).filter(Boolean) : [],
    cacheHit,
    slowToolReason: trimStr(payload.slowToolReason || payload.slow_tool_reason) || null,
    packetKey: trimStr(payload.packetKey || payload.packet_key || row.packet_key) || null,
    explanationPacketKey: trimStr(payload.explanationPacketKey || payload.explanation_packet_key || row.explanation_packet_key) || null,
    explanationSource: trimStr(payload.explanationSource || payload.explanation_source || row.explanation_source) || null,
    explanationStatus: trimStr(payload.explanationStatus || payload.explanation_status || row.explanation_status) || null,
    payloadHash: trimStr(payload.payloadHash || payload.payload_hash || row.payload_hash) || null,
  }
}

export const mapInstantCardContextPacket = (
  rowOrResponse: unknown,
  cacheHit: boolean,
): CfoAiContextPacket | null => {
  const row = isRecord(rowOrResponse)
    ? (isRecord((rowOrResponse as any).packet) ? (rowOrResponse as any).packet : rowOrResponse)
    : null
  if (!row) return null
  const payload = isRecord(row.payload_json)
    ? row.payload_json
    : isRecord(row.payload)
      ? row.payload
      : row
  const cardKey = trimStr(payload.cardKey || payload.card_key || row.card_key || row.cardKey)
  if (!cardKey) return null
  const rawExplanations = Array.isArray(payload.changeExplanations)
    ? payload.changeExplanations
    : Array.isArray(payload.change_explanations)
      ? payload.change_explanations
      : Array.isArray(row.explanations)
        ? row.explanations.map((item: any) => item?.payload_json || item)
        : Array.isArray(row.payload_json?.changeExplanations)
          ? row.payload_json.changeExplanations
          : []
  const changeExplanations = rawExplanations
    .map(mapChangeExplanation)
    .filter((item: CfoInstantCardChangeExplanation | null): item is CfoInstantCardChangeExplanation => item != null)
    .slice(0, 4)
  const status = trimStr(payload.status || row.status || (cacheHit ? 'ready' : 'unsupported')) || 'unsupported'
  const bytes = Number(payload.contextPacketBytes ?? payload.context_packet_bytes ?? row.context_packet_bytes)
  const latency = Number(payload.buildLatencyMs ?? payload.build_latency_ms ?? row.build_latency_ms)
  return {
    cardKey,
    freshnessToken: trimStr(payload.freshnessToken || payload.freshness_token || row.freshness_token) || null,
    surface: trimStr(payload.surface || row.surface) || null,
    metricId: trimStr(payload.metricId || payload.metric_id || row.metric_id) || null,
    period: trimStr(payload.period || row.period) || null,
    comparison: trimStr(payload.comparison || row.comparison) || null,
    status,
    primaryMetric: isRecord(payload.primaryMetric)
      ? payload.primaryMetric as CfoAiContextPacket['primaryMetric']
      : isRecord(payload.primary_metric)
        ? payload.primary_metric as CfoAiContextPacket['primaryMetric']
        : null,
    supportingFacts: Array.isArray(payload.supportingFacts)
      ? payload.supportingFacts as CfoAiContextPacket['supportingFacts']
      : Array.isArray(payload.supporting_facts)
        ? payload.supporting_facts as CfoAiContextPacket['supportingFacts']
        : null,
    changeExplanations,
    trend: isRecord(payload.trend) ? payload.trend as CfoAiContextPacket['trend'] : null,
    comparisons: Array.isArray(payload.comparisons) ? payload.comparisons : null,
    sourceArtifactIds: Array.isArray(payload.sourceArtifactIds)
      ? payload.sourceArtifactIds.map(trimStr).filter(Boolean)
      : Array.isArray(payload.source_artifact_ids)
        ? payload.source_artifact_ids.map(trimStr).filter(Boolean)
        : null,
    contextPacketBytes: Number.isFinite(bytes) ? bytes : null,
    buildLatencyMs: Number.isFinite(latency) ? latency : null,
    cacheHit,
    source: trimStr(payload.source || row.source) || 'deterministic_context_packet',
    packetKey: trimStr(payload.packetKey || payload.packet_key || `${cardKey}:context`) || null,
  }
}

export const compactAiContextPacketForAgent = (
  packet: CfoAiContextPacket | null | undefined,
): Record<string, unknown> | null => {
  if (!packet) return null
  const shortText = (value: unknown, max = 220): string => {
    const text = trimStr(value)
    return text.length > max ? `${text.slice(0, max - 1)}…` : text
  }
  const trendPoints = (packet.trend?.points || []).slice(-12).map((point) => ({
    label: shortText(point.label, 40),
    period: shortText((point as any).period, 40) || null,
    value: shortText(point.value, 64),
    raw_value: Number.isFinite(Number((point as any).rawValue ?? (point as any).raw_value))
      ? Number((point as any).rawValue ?? (point as any).raw_value)
      : null,
  }))
  return {
    card_key: packet.cardKey,
    freshness_token: packet.freshnessToken || null,
    surface: packet.surface || null,
    metric_id: packet.metricId || null,
    period: packet.period || null,
    comparison: packet.comparison || null,
    status: packet.status || null,
    primary_metric: packet.primaryMetric
      ? {
          label: shortText(packet.primaryMetric.label, 80),
          value: shortText(packet.primaryMetric.value, 80),
          raw_value: packet.primaryMetric.rawValue ?? null,
          tone: packet.primaryMetric.tone ?? null,
        }
      : null,
    supporting_facts: (packet.supportingFacts || []).slice(0, 4).map((fact) => ({
      label: shortText(fact.label, 80),
      value: shortText(fact.value, 120),
      tone: fact.tone ?? null,
    })),
    trend: packet.trend
      ? {
          label: shortText(packet.trend.label, 80),
          points: trendPoints,
          unavailable_reason: shortText(packet.trend.unavailableReason, 180) || null,
        }
      : null,
    comparisons: (packet.comparisons || []).slice(0, 4).map((row) => ({
      label: shortText(row.label, 80),
      current: shortText(row.current, 80),
      prior: shortText(row.prior, 80),
      change: shortText(row.change, 80),
      tone: row.tone ?? null,
      unavailable_reason: shortText(row.unavailableReason, 180) || null,
    })),
    change_explanations: (packet.changeExplanations || []).slice(0, 4).map((explanation) => ({
      period_kind: explanation.periodKind,
      label: shortText(explanation.label, 80),
      status: explanation.status,
      current: shortText(explanation.current, 80),
      prior: shortText(explanation.prior, 80),
      change: shortText(explanation.change, 80),
      headline: shortText(explanation.headline, 220),
      drivers: (explanation.drivers || []).slice(0, 4).map((driver) => ({
        id: shortText(driver.id, 80),
        label: shortText(driver.label, 90),
        value: shortText(driver.value, 80),
        raw_value: driver.rawValue ?? null,
        share_of_change: driver.shareOfChange ?? null,
        direction: driver.direction,
        category: driver.category ?? null,
        target: driver.target ?? null,
      })),
      confidence: explanation.confidence,
      caveats: (explanation.caveats || []).slice(0, 2).map((item) => shortText(item, 140)),
      recommended_action: shortText(explanation.recommendedAction, 220) || null,
    })),
    source_artifact_ids: (packet.sourceArtifactIds || []).slice(0, 5),
    context_packet_bytes: packet.contextPacketBytes ?? null,
    build_latency_ms: packet.buildLatencyMs ?? null,
    cache_hit: packet.cacheHit ?? null,
    source: packet.source || null,
  }
}

const enrichmentQueryForSession = (session: CfoAnalysisActiveSession) => ({
  surface: trimStr(session.instantCard?.surface),
  cardKey: trimStr(session.instantCard?.cardKey),
  metricId: trimStr(session.instantCard?.sourceLineage?.metricId || session.context.metricId),
  period: trimStr(session.instantCard?.period || session.context.period),
  comparison: trimStr(session.instantCard?.comparison || session.context.comparison),
  freshnessToken: trimStr(session.instantCard?.freshness?.sourceFingerprint),
  limit: 1,
})

const clientContextPacketFromCard = (session: CfoAnalysisActiveSession, status: string = 'warm_build'): CfoAiContextPacket | null => {
  const card = session.instantCard
  if (!card) return null
  const byPeriod = new Map<string, CfoInstantCardChangeExplanation>()
  for (const item of card.changeExplanations || []) {
    byPeriod.set(trimStr(item.periodKind) || trimStr(item.label).toLowerCase(), item)
  }
  for (const row of card.comparisons || []) {
    const label = trimStr(row.label)
    if (!label) continue
    const periodKind = /last 3/i.test(label)
      ? 'last_3_months'
      : /ytd|year to date/i.test(label)
        ? 'ytd'
        : /prior year|yoy/i.test(label)
          ? 'prior_year'
          : 'current_month'
    if (byPeriod.has(periodKind)) continue
    byPeriod.set(periodKind, {
      periodKind,
      label,
      status: row.unavailableReason ? 'unsupported' : 'partial',
      current: trimStr(row.current) || '-',
      prior: trimStr(row.prior) || '-',
      change: trimStr(row.change) || '-',
      headline: row.unavailableReason
        ? `${label} context is not available in the current card facts.`
        : `${card.primaryMetric.label} changed ${row.change || '-'} for ${label}; use this as context for selecting the most important CFO insight.`,
      drivers: [],
      confidence: row.unavailableReason ? 'low' : 'medium',
      caveats: row.unavailableReason ? [row.unavailableReason] : undefined,
      recommendedAction: 'Use this window to decide whether the selected movement is isolated or part of a broader trend.',
    })
  }
  const changeExplanations = ['current_month', 'last_3_months', 'ytd', 'prior_year']
    .map((key) => byPeriod.get(key))
    .filter((item): item is CfoInstantCardChangeExplanation => Boolean(item))
  return {
    cardKey: card.cardKey,
    freshnessToken: card.freshness?.sourceFingerprint ?? null,
    surface: card.surface,
    metricId: card.sourceLineage?.metricId || session.context.metricId || null,
    period: card.period || session.context.period || null,
    comparison: card.comparison || session.context.comparison || null,
    status,
    changeExplanations,
    primaryMetric: card.primaryMetric,
    supportingFacts: card.supportingFacts || null,
    trend: card.trend || null,
    comparisons: card.comparisons || null,
    sourceArtifactIds: card.sourceLineage?.artifactIds || null,
    contextPacketBytes: null,
    buildLatencyMs: 0,
    cacheHit: false,
    source: 'client_card_context_fallback',
    packetKey: `${card.cardKey}:client_context`,
  }
}

const actionKey = (action: Pick<CfoInstantCardAction, 'kind' | 'label'>) =>
  `${trimStr(action.kind) || 'ask'}:${trimStr(action.label).toLowerCase()}`

const normalizeCardAction = (
  action: CfoInstantCardAction,
  session: CfoAnalysisActiveSession,
): CfoInstantCardAction | null => {
  const label = trimStr(action?.label)
  const prompt = trimStr(action?.prompt)
  if (!label || !prompt) return null
  return {
    ...action,
    kind: trimStr(action.kind) || 'ask',
    label,
    prompt,
    target: action.target || { surface: session.context.activeSection, metricId: session.context.metricId || session.instantCard?.sourceLineage?.metricId || null },
  }
}

export const fallbackNextLevelOptions = (session: CfoAnalysisActiveSession): CfoInstantCardAction[] => {
  const card = session.instantCard
  if (!card) return []
  const explicitNextSteps = (card.nextSteps || [])
    .map((action) => normalizeCardAction(action, session))
    .filter((action): action is CfoInstantCardAction => Boolean(action))
  if (explicitNextSteps.length) {
    const deduped = new Map<string, CfoInstantCardAction>()
    for (const action of explicitNextSteps) deduped.set(actionKey(action), action)
    for (const action of card.actions || []) {
      const normalized = normalizeCardAction(action, session)
      if (normalized && !deduped.has(actionKey(normalized))) deduped.set(actionKey(normalized), normalized)
      if (deduped.size >= 4) break
    }
    return Array.from(deduped.values()).slice(0, 4)
  }
  const actionsByKind = new Map<string, CfoInstantCardAction>()
  for (const action of card.actions || []) {
    const normalized = normalizeCardAction(action, session)
    const kind = normalized?.kind ?? 'ask'
    if (normalized && !actionsByKind.has(kind)) actionsByKind.set(kind, normalized)
  }
  const metricLabel = trimStr(session.context.metricLabel || card.title) || 'this metric'
  const typedRoutes: CfoInstantCardAction[] = []
  const drilldown = actionsByKind.get('drilldown')
  if (drilldown) {
    typedRoutes.push({
      ...drilldown,
      label: 'Review driver rollup',
      prompt: `Review the bounded driver rollup for ${metricLabel}; show the largest account, category, customer, or service-line contributors only when already available.`,
    })
  }
  const transactions = actionsByKind.get('transactions')
  if (transactions) {
    typedRoutes.push({
      ...transactions,
      label: 'Open detail route',
      prompt: `Open the governed detail route for ${metricLabel} using only the selected metric and period identifiers.`,
    })
  }
  const compare = actionsByKind.get('compare')
  if (compare) {
    typedRoutes.push({
      ...compare,
      label: 'Stress-test comparison',
      prompt: `Compare ${metricLabel} across current month, trailing periods, YTD, and prior year where those windows are available.`,
    })
  }
  const deduped = new Map<string, CfoInstantCardAction>()
  for (const action of typedRoutes) {
    deduped.set(actionKey(action), action)
  }
  if (deduped.size === 0) {
    for (const action of [...(card.nextSteps || []), ...(card.actions || [])]) {
      const normalized = normalizeCardAction(action, session)
      if (normalized && !deduped.has(actionKey(normalized))) deduped.set(actionKey(normalized), normalized)
    }
  }
  return Array.from(deduped.values()).slice(0, 4)
}

const fallbackEnrichmentFromInstantCard = (
  session: CfoAnalysisActiveSession,
  reason: string,
  latencyMs: number | null = null,
): CfoInstantCardEnrichment | null => {
  const card = session.instantCard
  if (!card) return null
  const suggestedOptions = fallbackNextLevelOptions(session)
  return {
    cardKey: card.cardKey,
    freshnessToken: card.freshness?.sourceFingerprint ?? null,
    status: 'ready',
    suggestedOptions,
    // No user-facing summary facts: the old "Ready from / Card facts / Packet /
    // Prepared in {ms} / Options" entries were internal plumbing that leaked into
    // the UI. The deterministic verdict card carries the read; suggestedOptions
    // fold silently into the card's next-step chips.
    summaryFacts: [],
    changeExplanations: card.changeExplanations || [],
    latencyMs,
    source: 'client_card_fallback',
    toolNames: [],
    cacheHit: false,
    slowToolReason: reason,
    packetKey: card.cardKey,
    explanationPacketKey: `${card.cardKey}:fallback_explanations`,
    explanationSource: 'instant_card',
    explanationStatus: card.changeExplanations?.some((item) => item.status === 'ready' || item.status === 'partial') ? 'ready' : 'unsupported',
  }
}

export const deterministicComparisonEnrichmentFromCard = (
  session: CfoAnalysisActiveSession,
  action?: CfoInstantCardAction | null,
): CfoInstantCardEnrichment | null => {
  const card = session.instantCard
  const rows = (card?.comparisons || []).filter((row) => trimStr(row.label) && (trimStr(row.current) || trimStr(row.change)))
  if (!card || !rows.length) return null
  const metricLabel = trimStr(session.context.metricLabel || card.primaryMetric.label || card.title) || 'Metric'
  const suggestedOptions = [...(card.actions || []), ...(card.nextSteps || [])]
    .map((candidate) => normalizeCardAction(candidate, session))
    .filter((candidate): candidate is CfoInstantCardAction => Boolean(candidate))
    .filter((candidate) => candidate.kind !== 'compare')
    .slice(0, 4)
  if (action?.kind && action.kind !== 'compare') {
    const normalized = normalizeCardAction(action, session)
    if (normalized) suggestedOptions.unshift(normalized)
  }
  return {
    cardKey: card.cardKey,
    freshnessToken: card.freshness?.sourceFingerprint ?? null,
    status: 'ready',
    suggestedOptions,
    summaryFacts: rows.slice(0, 4).map((row) => ({
      label: row.label,
      value: `${row.current || '-'} vs ${row.prior || '-'}${row.change ? ` (${row.change})` : ''}`,
      tone: row.tone || 'neutral',
    })),
    changeExplanations: card.changeExplanations || [],
    latencyMs: 0,
    source: 'deterministic_compare',
    toolNames: [],
    cacheHit: true,
    slowToolReason: null,
    packetKey: `${card.cardKey}:compare`,
    explanationPacketKey: `${card.cardKey}:compare_explanations`,
    explanationSource: 'instant_card',
    explanationStatus: card.changeExplanations?.some((item) => item.status === 'ready' || item.status === 'partial') ? 'ready' : 'unsupported',
    payloadHash: `${card.cardKey}:compare:${rows.map((row) => `${row.label}:${row.current}:${row.prior}:${row.change}`).join('|')}`,
  }
}

// The standalone "Next-level options" enrichment panel was removed: it leaked
// internal packet plumbing (packet source, "Prepared in {ms}", option counts,
// metric family) to the user. The warm packet's `suggestedOptions` are now
// folded silently into the verdict card's next-step chips via `extraActions`,
// and the deterministic verdict card carries the read.

const looksLikeWorkspaceContext = (value: unknown): value is Record<string, any> => {
  if (!isRecord(value)) return false
  return Boolean(
    value.interaction ||
      value.source_component ||
      value.valuation_summary ||
      value.valuation_industry_context ||
      value.company_context ||
      value.valuation_market_benchmark ||
      value.driver,
  )
}

const isNetIncomeBridgeEvidenceClick = (session: CfoAnalysisActiveSession): boolean =>
  session.mode === 'evidence' && sourceComponentForSession(session).startsWith('net_income_bridge')

const isNetIncomeBridgeBarEvidenceClick = (session: CfoAnalysisActiveSession): boolean =>
  session.mode === 'evidence' && sourceComponentForSession(session) === 'net_income_bridge_bar'

const isBridgeAllowedTool = (toolName: unknown): boolean => {
  switch (trimStr(toolName)) {
    case 'list_agent_artifacts':
    case 'run_drilldown_recipe':
    case 'get_metric_definition':
    case 'get_cfo_page_contract':
    case 'get_cfo_metric_evidence':
      return true
    default:
      return false
  }
}

const primitiveOnly = (value: unknown): unknown => {
  if (value == null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value
  return undefined
}

const compactSelectionForAgent = (value: unknown): Record<string, unknown> | null => {
  if (!isRecord(value)) return null
  const out: Record<string, unknown> = {}
  const keys = [
    'selection_key',
    'selectionKey',
    'metric_id',
    'metric_label',
    'source_component',
    'row_key',
    'rowKey',
    'account_id',
    'account_name',
    'customer_id',
    'customer_name',
    'scenario_id',
    'report_block_id',
    'period',
    'current_period_end',
    'prior_period_end',
    'comparison',
    'comparison_label',
    'value',
    'display',
    'current_value',
    'prior_value',
    'delta',
    'delta_abs',
    'delta_pct',
    'metric_delta',
    'net_income_effect',
    'source_fingerprint',
    'signature',
    'deterministic_read',
  ]
  for (const key of keys) {
    const v = primitiveOnly((value as any)[key])
    if (v !== undefined && trimStr(v)) out[key] = v
  }
  if (Array.isArray((value as any).artifact_ids)) {
    out.artifact_ids = (value as any).artifact_ids.map((item: unknown) => trimStr(item)).filter(Boolean).slice(0, 4)
  }
  return Object.keys(out).length ? out : null
}

const compactGroundingForAgent = (grounding: unknown): Record<string, unknown> | null => {
  if (!isRecord(grounding)) return null
  const out: Record<string, unknown> = {}
  for (const key of [
    'metric_id',
    'metric_label',
    'value_kind',
    'latest',
    'latest_period',
    'display',
    'healthy_min',
    'healthy_max',
    'higher_is_better',
    'assessment',
    'benchmark_basis',
    'benchmark_label',
    'formula',
    'interpretation',
    'source',
  ]) {
    const v = primitiveOnly((grounding as any)[key])
    if (v !== undefined && (typeof v !== 'string' || trimStr(v))) out[key] = v
  }
  if (isRecord((grounding as any).mom)) out.mom = (grounding as any).mom
  if (Array.isArray((grounding as any).comparisons)) {
    out.comparisons = (grounding as any).comparisons.slice(0, 4).map((row: any) => ({
      label: row?.label ?? null,
      current: row?.current ?? null,
      prior: row?.prior ?? null,
      delta_pct: row?.delta_pct ?? null,
    }))
  }
  if (Array.isArray((grounding as any).components)) {
    out.components = (grounding as any).components.slice(0, 2).map((component: any) => ({
      label: component?.label ?? null,
      role: component?.role ?? null,
      first: component?.first ?? null,
      last: component?.last ?? null,
      delta_pct: component?.delta_pct ?? null,
      top_accounts: Array.isArray(component?.top_accounts)
        ? component.top_accounts.slice(0, 3).map((account: any) => ({
            label: account?.label ?? null,
            value: account?.value ?? null,
            delta_abs: account?.delta_abs ?? null,
            share: account?.share ?? null,
          }))
        : [],
    }))
  }
  return Object.keys(out).length ? out : null
}

const compactInitialVisualForAgent = (session: CfoAnalysisActiveSession): Record<string, unknown> | null => {
  const visual = session.visual
  if (!visual) return null
  if (visual.kind === 'value') return { kind: 'value', label: visual.label, display: visual.display, value: visual.value ?? null }
  if (visual.kind === 'ratio_health') return {
    kind: 'ratio_health',
    label: visual.label,
    display: visual.display,
    value: visual.value,
    assessment: visual.assessment,
  }
  return {
    kind: 'chart',
    caption: visual.caption ?? null,
    chart_kind: visual.chart?.kind ?? null,
    title: visual.chart?.title ?? null,
  }
}

const measureBytes = (value: unknown): number => {
  try {
    return new TextEncoder().encode(JSON.stringify(value)).length
  } catch {
    return JSON.stringify(value ?? '').length
  }
}

const copyHtmlToClipboard = async (html: string): Promise<boolean> => {
  if (!trimStr(html)) return false
  if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([html], { type: 'text/plain' }),
        }),
      ])
      return true
    } catch {
      // Fall through to copying the HTML source as plain text.
    }
  }
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(html)
      return true
    } catch {
      // Fall through to the textarea path.
    }
  }
  try {
    const textarea = document.createElement('textarea')
    textarea.value = html
    textarea.setAttribute('readonly', 'true')
    textarea.style.position = 'fixed'
    textarea.style.left = '-9999px'
    textarea.style.top = '0'
    document.body.appendChild(textarea)
    textarea.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(textarea)
    return ok
  } catch {
    return false
  }
}

const visibleMessagesForSession = (
  session: CfoAnalysisActiveSession,
  messages: AiChatMessage[],
): AiChatMessage[] => {
  if (session.mode !== 'evidence') return messages
  const seedPrompt = trimStr(session.seedPrompt)
  const enrichmentPrompt = session.instantCard ? trimStr(instantCardEnrichmentPrompt(session)) : ''
  let hidSeedPrompt = false
  let hidEnrichmentPrompt = false
  let suppressEnrichmentAssistant = false
  return messages.filter((message) => {
    if (suppressEnrichmentAssistant && message.role === 'assistant') {
      return false
    }
    if (suppressEnrichmentAssistant && message.role === 'user') {
      suppressEnrichmentAssistant = false
    }
    if (!hidSeedPrompt && message.role === 'user' && seedPrompt && trimStr(message.content) === seedPrompt) {
      hidSeedPrompt = true
      return false
    }
    if (!hidEnrichmentPrompt && message.role === 'user' && enrichmentPrompt && trimStr(message.content) === enrichmentPrompt) {
      hidEnrichmentPrompt = true
      suppressEnrichmentAssistant = true
      return false
    }
    if (
      isNetIncomeBridgeEvidenceClick(session) &&
      !isNetIncomeBridgeBarEvidenceClick(session) &&
      message.role === 'assistant' &&
      message.metadata?.kind === 'final_headline'
    ) {
      return false
    }
    return true
  })
}

const firstDrilldownMetadata = (messages: AiChatMessage[]): { manifestVersion: string | null; recipeId: string | null } => {
  for (const message of messages) {
    const metadata = message.metadata || {}
    const manifestVersion = trimStr((metadata as any).drilldown_manifest_version)
    const recipeId = trimStr((metadata as any).recipe_id)
    if (manifestVersion || recipeId) return { manifestVersion: manifestVersion || null, recipeId: recipeId || null }
  }
  return { manifestVersion: null, recipeId: null }
}

export type CfoAnalysisModalProps = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  session: CfoAnalysisActiveSession
  history: CfoPersistedSession[]
  historyQuery: string
  onHistoryQueryChange: (q: string) => void
  historyArchived: boolean
  onToggleHistoryArchived: (archived: boolean) => void
  onClose: () => void
  onReopen: (session: CfoPersistedSession) => void
  onContinue: (session: CfoAnalysisActiveSession, seedPrompt?: string | null) => void
  onPersistSession: (record: CfoPersistedSession) => void
  onDeleteSession: (id: string) => void
  onArchiveSession: (id: string, archived: boolean) => void
  onTagToReport: (item: Partial<CfoReportAnalysisItem>) => Promise<void>
  onOpenReport: (period?: string | null) => void
  prefetchedEnrichment?: CfoInstantCardEnrichment | null
  onEnrichmentPacketReady?: (packet: CfoInstantCardEnrichment) => void
}

export function CfoAnalysisModal({
  appApi,
  workerClient = null,
  workbookId,
  session,
  history,
  historyQuery,
  onHistoryQueryChange,
  historyArchived,
  onToggleHistoryArchived,
  onClose,
  onReopen,
  onContinue,
  onPersistSession,
  onDeleteSession,
  onArchiveSession,
  onTagToReport,
  onOpenReport,
  prefetchedEnrichment = null,
  onEnrichmentPacketReady,
}: CfoAnalysisModalProps) {
  const surface = `${session.context.activeSection}:copilot`
  const isRestored = !!session.restored
  const [historyOpen, setHistoryOpen] = useState(false)
  const [tagged, setTagged] = useState(false)
  const [tagBusy, setTagBusy] = useState(false)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const [enrichment, setEnrichment] = useState<CfoInstantCardEnrichment | null>(null)
  const [contextPacket, setContextPacket] = useState<CfoAiContextPacket | null>(null)
  // Next-level decomposition of a clicked bridge driver (segments / statement
  // categories) fetched from the governed drilldown recipes — the card grafts
  // it in place of the sibling waterfall the user already saw on the page.
  // Both lenses are kept: `accounts` follows the period switcher; `segments`
  // (service-line direct CM) is MoM-only and gated on configured rollups.
  const [accountsBreakdown, setAccountsBreakdown] = useState<CfoNextLevelBreakdown | null>(null)
  const [segmentBreakdown, setSegmentBreakdown] = useState<CfoNextLevelBreakdown | null>(null)
  const [nextLevelBusy, setNextLevelBusy] = useState(false)
  const [nextLevelComparison, setNextLevelComparison] = useState<string>(
    () => normalizeCfoDrilldownComparisonKind(session.context.comparison) ?? 'mom',
  )
  const [nextLevelView, setNextLevelView] = useState<'accounts' | 'segments'>('accounts')
  // Full RatioCard backing the embedded ratio detail card. Provided directly
  // when opened from the Ratios page (selection.ratio_card), else lazily fetched
  // for a ratio_health session so the rich card slots in below the instant gauge.
  const [embeddedRatio, setEmbeddedRatio] = useState<RatioCard | null>(null)
  // Follow the ratio window the caller was viewing (the Ratios page passes it in selection.ratio_window,
  // possibly nested one level deep) so the embedded detail card matches the page. Defaults to the
  // page-wide default ("Latest month") for sessions opened without an explicit window (e.g. from Home).
  const embeddedRatioWindow = useMemo<RatioWindow>(() => {
    const parse = (value: unknown): RatioWindow | null =>
      RATIO_WINDOW_OPTIONS.some((option) => option.id === value) ? (value as RatioWindow) : null
    const selection = session.context.selection as Record<string, any> | null
    const nested = isRecord(selection?.selection) ? selection?.selection : null
    return parse(selection?.ratio_window) || parse(nested?.ratio_window) || 'latest'
  }, [session.context.selection])
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const composerRef = useRef<HTMLTextAreaElement | null>(null)
  const sentRef = useRef(false)
  const nextLevelSentRef = useRef('')
  const enrichmentSentRef = useRef('')
  const enrichmentAgentRunIdRef = useRef('')
  const enrichmentStartedAtRef = useRef(0)
  const persistSigRef = useRef('')
  const buildProgressMessages = useCallback(
    (...args: Parameters<typeof buildCfoAnalysisProgressMessages>) => {
      const [result] = args
      if (isNetIncomeBridgeEvidenceClick(session) && !isBridgeAllowedTool(result.name)) return []
      const selection = session.context.selection
      const selectedNetIncomeEffect =
        selection && typeof selection === 'object' && Number.isFinite(Number(selection.net_income_effect))
          ? Number(selection.net_income_effect)
          : null
      const selectedDelta =
        selection && typeof selection === 'object' && Number.isFinite(Number(selection.delta))
          ? Number(selection.delta)
          : null
      const hydratedResult =
        isNetIncomeBridgeEvidenceClick(session) && result.name === 'explain_metric_change' && result.result && typeof result.result === 'object'
          ? {
              ...result,
              result: {
                ...(result.result as Record<string, unknown>),
                selected_waterfall_delta: (result.result as Record<string, unknown>).selected_waterfall_delta ?? selectedDelta,
                selected_net_income_effect:
                  (result.result as Record<string, unknown>).selected_net_income_effect ?? selectedNetIncomeEffect,
              },
            }
          : result
      return buildCfoAnalysisProgressMessages(hydratedResult, args[1], args[2])
    },
    [session],
  )

  const copilot = useCfoCopilot({
    appApi,
    workerClient,
    workbookId,
    activeSection: session.page.activeSection,
    duckDbOpts: session.page.duckDbOpts ?? null,
    getComparisons: session.page.getComparisons,
    suggestedPrompts: session.page.suggestedPrompts ?? [],
    allowLocalDuckDbTools: true,
    buildProgressMessages,
  })
  const live = copilot
  const { input, setInput, sendMessage } = copilot
  // The active lens: segment CM when the user toggled to it (and it exists),
  // else the statement accounts breakdown at the selected comparison window.
  const activeNextLevel = nextLevelView === 'segments' && segmentBreakdown ? segmentBreakdown : accountsBreakdown
  // The card the modal renders and forwards to the agent: the deterministic
  // instant card, upgraded in place once the next-level breakdown lands.
  const displayCard = useMemo(
    () => (session.instantCard && activeNextLevel ? applyCfoNextLevelBreakdown(session.instantCard, activeNextLevel) : session.instantCard),
    [activeNextLevel, session.instantCard],
  )
  const nextLevelEligible =
    !isRestored && session.mode === 'evidence' && !!session.instantCard && shouldFetchCfoNextLevelBreakdown(session.context)
  // Reset the period switcher + lens when the modal opens on a new session.
  const nextLevelViewChosenRef = useRef(false)
  useEffect(() => {
    setAccountsBreakdown(null)
    setSegmentBreakdown(null)
    setNextLevelView('accounts')
    setNextLevelComparison(normalizeCfoDrilldownComparisonKind(session.context.comparison) ?? 'mom')
    nextLevelViewChosenRef.current = false
  }, [session.id])
  // Fetch the next level behind a clicked bridge driver (Revenue → segment
  // contribution or statement categories; COGS/Opex → their category/account
  // movement). Guarded per session+comparison so instant-card hydration doesn't
  // refetch; a late resolve on a closed modal is a harmless no-op setState.
  useEffect(() => {
    if (!nextLevelEligible) return
    const fetchKey = `${session.id}:${nextLevelComparison}`
    if (nextLevelSentRef.current === fetchKey) return
    nextLevelSentRef.current = fetchKey
    setNextLevelBusy(true)
    void fetchCfoNextLevelBreakdown({
      appApi,
      workerClient,
      duckDbOpts: session.page.duckDbOpts ?? null,
      workbookId,
      context: session.context,
      comparison: nextLevelComparison,
    })
      .then(({ accounts, segments }) => {
        if (nextLevelSentRef.current !== fetchKey) return
        if (accounts) setAccountsBreakdown(accounts)
        // Segments are only attempted on MoM fetches; keep the last known
        // segment lens across period switches so the toggle stays offered.
        if (segments) {
          setSegmentBreakdown(segments)
          // Configured service lines are the highest-signal lens — lead with
          // them (as the page-level CM panel does) unless the user has already
          // driven the controls in this session.
          if (!nextLevelViewChosenRef.current) setNextLevelView('segments')
        }
      })
      .catch(() => {
        /* keep the honest "drivers warming" skeleton; the agent stream still answers */
      })
      .finally(() => {
        if (nextLevelSentRef.current === fetchKey) setNextLevelBusy(false)
      })
  }, [appApi, isRestored, nextLevelComparison, nextLevelEligible, session, workerClient, workbookId])
  // Period switch: statement lens follows any window; segment CM is MoM-only,
  // so leaving MoM drops back to the accounts lens.
  const handleNextLevelComparison = useCallback((kind: string) => {
    nextLevelViewChosenRef.current = true
    const normalized = normalizeCfoDrilldownComparisonKind(kind) ?? 'mom'
    setNextLevelComparison(normalized)
    if (normalized !== 'mom') setNextLevelView('accounts')
  }, [])
  const handleNextLevelView = useCallback((view: string) => {
    nextLevelViewChosenRef.current = true
    const next = view === 'segments' ? 'segments' : 'accounts'
    setNextLevelView(next)
    // Segment CM compares latest vs prior month only — snap the period chip
    // back to MoM so the grid and its labels stay truthful.
    if (next === 'segments') setNextLevelComparison('mom')
  }, [])
  // Period switcher + lens toggle rendered inside the card's driver strip —
  // same interaction model as the Financials segment-CM panel.
  const nextLevelControls = nextLevelEligible && (accountsBreakdown || segmentBreakdown || nextLevelBusy) ? (
    <div className="cfo-analysis-lens-controls">
      {segmentBreakdown ? (
        <CfoShellSelectChip
          className="cfo-analysis-lens-chip"
          label="View"
          ariaLabel="Breakdown lens"
          value={nextLevelView === 'segments' ? 'segments' : 'accounts'}
          options={[{ id: 'accounts', label: 'By account' }, { id: 'segments', label: 'By segment' }]}
          onChange={handleNextLevelView}
        />
      ) : null}
      <CfoShellSelectChip
        className="cfo-analysis-lens-chip"
        label="Period"
        ariaLabel="Comparison period"
        value={nextLevelComparison}
        options={NEXT_LEVEL_COMPARISON_OPTIONS}
        onChange={handleNextLevelComparison}
      />
      {nextLevelBusy ? <span className="cfo-analysis-lens-busy" role="status" aria-label="Updating breakdown" /> : null}
    </div>
  ) : null
  const sendWithSessionContext = useCallback(
    (
      text: string,
      interaction: string = 'ask',
      action?: CfoInstantCardAction | null,
      telemetryContext?: Record<string, unknown> | null,
    ) => {
      const extraContext = looksLikeWorkspaceContext(session.context.selection) ? session.context.selection : null
      const sourceTables = session.context.sourceTables || (Array.isArray(extraContext?.source_tables) ? extraContext.source_tables : null)
      const sourceComponent = trimStr(extraContext?.source_component) || session.context.sourceComponent || null
      const nestedSelectionRaw = extraContext
        ? (isRecord(extraContext.selection) ? extraContext.selection : null)
        : session.context.selection || null
      const nestedSelection = compactSelectionForAgent(nestedSelectionRaw) || compactSelectionForAgent(session.context.selection)
      const grounding = embeddedRatio
        ? compactGroundingForAgent(groundingFromRatioCard(embeddedRatio, embeddedRatioWindow))
        : compactGroundingForAgent(session.context.grounding)
      const instantCard = compactInstantCardForAgent(displayCard)
      const contextPacketOverride = isRecord(telemetryContext?.context_packet) ? telemetryContext?.context_packet as any : null
      const selectedContextPacket = contextPacketOverride || contextPacket
      const { context_packet: _rawContextPacket, ...telemetryFields } = telemetryContext || {}
      const aiContextPacket = interaction === 'context_insight'
        ? compactAiContextPacketForAgent(selectedContextPacket as CfoAiContextPacket | null)
        : null
      const workspaceContext: Record<string, unknown> = {
        interaction: action?.kind ? 'instant_card_action' : interaction === 'instant_card_enrich' ? 'instant_card_enrich' : interaction === 'context_insight' ? 'context_insight' : trimStr(extraContext?.interaction) || interaction,
        action_kind: action?.kind || null,
        action_target: action?.target || null,
        enrichment_mode: interaction === 'instant_card_enrich' ? 'fast_lane_options' : null,
        context_mode: interaction === 'context_insight' ? 'deterministic_packet' : null,
        source_component: sourceComponent,
        metric_id: session.context.metricId || action?.target?.metricId || null,
        metric_label: session.context.metricLabel || null,
        period: session.context.period || null,
        comparison: session.context.comparison || null,
        source_tables: Array.isArray(sourceTables) ? sourceTables.slice(0, 6) : null,
        selection: nestedSelection,
        initial_visual: compactInitialVisualForAgent(session),
        instant_card: instantCard,
        context_packet: aiContextPacket,
        grounding,
        ...telemetryFields,
      }
      const contextBudgetBytes = interaction === 'context_insight'
        ? CONTEXT_INSIGHT_CONTEXT_BUDGET_BYTES
        : EVIDENCE_CLICK_CONTEXT_BUDGET_BYTES
      const contextBytes = measureBytes(workspaceContext)
      if (instantCard && contextBytes > contextBudgetBytes) {
        workspaceContext.grounding = session.instantCard ? null : grounding
        workspaceContext.initial_visual = null
      }
      const finalBytes = measureBytes(workspaceContext)
      return sendMessage(text, {
        selected_metric_id: session.context.metricId || action?.target?.metricId || null,
        workspace_context: {
          ...workspaceContext,
          payload_budget: {
            compact_context_bytes: finalBytes,
            max_context_bytes: contextBudgetBytes,
          },
        },
      })
    },
    [sendMessage, session.context, session.visual, session.instantCard, displayCard, embeddedRatio, embeddedRatioWindow, contextPacket],
  )

  // In a restored (History) session we render the saved transcript read-only;
  // otherwise we drive the live agent.
  const messages = isRestored ? session.restored!.transcript : live.messages
  const visibleMessages = visibleMessagesForSession(session, messages)
  const hideFinalAnswer = isNetIncomeBridgeEvidenceClick(session) && !isNetIncomeBridgeBarEvidenceClick(session)
  const response = isRestored
    ? (({ answer: session.restored!.answer ?? undefined, visuals: session.restored!.visuals ?? [] } as unknown) as AiAgentContinueResponse)
    : live.response
  const isEnrichmentTurn =
    !isRestored &&
    !!session.instantCard &&
    !!enrichment &&
    (enrichment.status === 'loading' ||
      enrichment.status === 'slow' ||
      (!!enrichmentAgentRunIdRef.current && trimStr(response?.agent_run_id) === enrichmentAgentRunIdRef.current))
  const streamResponse = hideFinalAnswer || isEnrichmentTurn ? null : response
  const busy = isRestored ? false : live.busy
  const shouldShowSeedEvidence = Boolean(session.visual && !session.instantCard)
  // A run cancelled because the surface unmounted or a newer run started is an
  // expected lifecycle event (the user navigated or re-asked) — not a failure to
  // surface as a red error. Swallow it; show only genuine errors.
  const rawError = isRestored ? '' : live.error
  const error = /cancelled because the surface unmounted or a newer run started/i.test(rawError) ? '' : rawError
  // Open-ended follow-ups (the composer and question chips that feed the live
  // agent) are paused until the backend answers well enough; deterministic card
  // actions and evidence stay on.
  const openEndedAskEnabled = isOpenEndedAskEnabled()
  // Next-step chips: the agent's own followups win. For evidence clicks fall back
  // to deterministic ratio/metric chips (NOT the page's generic Ask prompts, which
  // are irrelevant to a specific metric). Plain Ask mode keeps the page prompts.
  const agentFollowups = (live.response?.followups || [])
    .map((followup) => ({ label: trimStr(followup.label), prompt: trimStr(followup.prompt) }))
    .filter((followup) => followup.label && followup.prompt)
  const deterministicFollowups = fallbackChipsForSession({ context: session.context, visual: session.visual })
  const cardFollowups = (session.instantCard?.actions || [])
    .map((action) => ({ label: trimStr(action.label), prompt: trimStr(action.prompt) }))
    .filter((action) => action.label && action.prompt)
  const promptChips = isRestored || !openEndedAskEnabled
    ? []
    : session.instantCard
      ? (agentFollowups.length && !isEnrichmentTurn ? agentFollowups.slice(0, 4) : [])
    : isNetIncomeBridgeBarEvidenceClick(session)
      ? (cardFollowups.length ? cardFollowups : deterministicFollowups).slice(0, 4)
      : agentFollowups.length
      ? agentFollowups.slice(0, 4)
      : session.mode === 'ask'
        ? live.promptChips
        : cardFollowups.length
          ? cardFollowups.slice(0, 4)
          : deterministicFollowups
  const restoredManifestVersion = trimStr(session.restored?.manifestVersion)
  const restoredRecipeId = trimStr(session.restored?.recipeId)
  const restoredUsesOlderManifest = Boolean(
    isRestored && restoredManifestVersion && restoredManifestVersion !== CFO_DRILLDOWN_MANIFEST.version,
  )

  const handleCardAction = useCallback((action: CfoInstantCardAction) => {
    if (action.kind === 'report') {
      setTagBusy(true)
      void onTagToReport({
        period: session.monthKey,
        title: displayCard?.title || action.label,
        summary: displayCard?.insights?.[0] || displayCard?.deterministicRead || action.prompt,
        surface: session.context.activeSection,
        metricLabel: session.context.metricLabel || displayCard?.primaryMetric.label || null,
        sourceLabel: displayCard?.sourceLineage?.sourceComponent || session.context.sourceComponent || null,
      } as any)
        .then(() => setTagged(true))
        .finally(() => setTagBusy(false))
      return
    }
    if (action.kind === 'open_surface' || action.kind === 'transactions') {
      const page = cfoPageForActionTarget(action)
      // Prefer the host page's inline GL view (a scoped modal over the current
      // page) to a full navigation away from the analysis session.
      if (page === 'gl-detail' && session.page.openGlDetail?.({
        metricId: trimStr(action.target?.metricId) || session.context.metricId || null,
        accountId: trimStr(action.target?.accountId) || null,
        metricLabel: session.context.metricLabel || null,
        // Scope to the clicked cell's month: prefer the action's own period,
        // then the selection's cell_period. Falls back to null (page's latest
        // period) for row/metric-level analysis with no single cell in focus.
        period:
          trimStr(action.target?.period) ||
          trimStr((session.context.selection as Record<string, any> | null)?.cell_period) ||
          null,
      })) {
        return
      }
      const targetMetricId = trimStr(action.target?.metricId) || session.context.metricId || null
      if (page && assignCfoPageUrl(page, {
        [CFO_ROUTE_FOCUS_METRIC_PARAM]: page === 'ratios' ? targetMetricId : null,
        metric_id: page === 'ratios' ? null : targetMetricId,
        account_id: action.target?.accountId,
        customer_id: action.target?.customerId,
      })) {
        return
      }
    }
    if (action.kind === 'compare') {
      const deterministic = deterministicComparisonEnrichmentFromCard(session, action)
      if (deterministic) {
        setEnrichment(deterministic)
        return
      }
    }
    void sendWithSessionContext(action.prompt, 'instant_card_action', action)
  }, [displayCard, onTagToReport, sendWithSessionContext, session])

  // Front-and-center escape hatches out of the modal: the governed statements
  // page for statement metrics, plus Ratios & trends when a metric/ratio is in
  // focus. These are page navigations, not agent prompts.
  const exploreLinks = useMemo(() => {
    const route = typeof window === 'undefined' ? null : parseCfoRouteState(window.location)
    if (!route) return []
    const isRatioSession =
      session.visual?.kind === 'ratio_health' ||
      session.context.grounding?.source === 'ratio_card' ||
      trimStr(session.context.sourceComponent).startsWith('ratio')
    const links: Array<{ page: CfoPageSlug; label: string }> = []
    if (route.page !== 'financials') links.push({ page: 'financials', label: 'Open Financials' })
    if ((isRatioSession || trimStr(session.context.metricId)) && route.page !== 'ratios') {
      links.push({ page: 'ratios', label: 'Open Ratios & trends' })
    }
    return links
  }, [session])

  const handleExploreLink = useCallback((page: CfoPageSlug) => {
    assignCfoPageUrl(page, {
      [CFO_ROUTE_FOCUS_METRIC_PARAM]: page === 'ratios' ? session.context.metricId : null,
      metric_id: page === 'ratios' ? null : session.context.metricId,
    })
  }, [session.context.metricId])

  // Auto-send the seeded prompt once (evidence open, or "continue" with a
  // prompt). Plain Ask and restored views wait for the user. The send is
  // deferred a tick and cleared on unmount so React StrictMode's dev
  // mount→unmount→remount cycle doesn't fire it twice (the first run would be
  // cancelled by the fake unmount, racing the second); only the stable mount's
  // timer survives, so exactly one run starts.
  useEffect(() => {
    if (isRestored) return
    if (session.mode === 'evidence' && session.instantCard) {
      const cardKey = trimStr(session.instantCard.cardKey)
      const enrichmentKey = `${session.id}:${cardKey}`
      if (!cardKey) return
      if (prefetchedEnrichment?.cardKey === cardKey && prefetchedEnrichment.status === 'ready') {
        setEnrichment((current) => {
          if (
            current?.cardKey === cardKey &&
            current.status === 'ready' &&
            current.payloadHash &&
            current.payloadHash === prefetchedEnrichment.payloadHash
          ) {
            return current
          }
          return {
            ...prefetchedEnrichment,
            latencyMs: prefetchedEnrichment.latencyMs ?? 0,
            cacheHit: prefetchedEnrichment.cacheHit ?? true,
            source: prefetchedEnrichment.source || 'prewarm',
          }
        })
        enrichmentSentRef.current = enrichmentKey
        return
      }
      if (enrichmentSentRef.current === enrichmentKey) return
      enrichmentSentRef.current = enrichmentKey
      enrichmentAgentRunIdRef.current = ''
      const freshnessToken = session.instantCard.freshness?.sourceFingerprint ?? null
      const startedAt = performance.now()
      const immediateFallback = fallbackEnrichmentFromInstantCard(session, 'warming', 0)
      setEnrichment(immediateFallback || {
        cardKey,
        freshnessToken,
        status: 'loading',
        suggestedOptions: [],
        summaryFacts: [],
        latencyMs: null,
        source: 'persisted_packet',
        toolNames: null,
        cacheHit: null,
      })
      let cancelled = false
      const slowHandle = window.setTimeout(() => {
        setEnrichment((current) => current && current.cardKey === cardKey && (current.status === 'loading' || current.status === 'preparing')
          ? { ...current, status: 'slow', slowToolReason: 'enrichment_packet_exceeded_1500ms' }
          : current)
      }, ENRICHMENT_SLOW_MS)
      const preparingHandle = window.setTimeout(() => {
        setEnrichment((current) => current && current.cardKey === cardKey && current.status === 'loading'
          ? { ...current, status: 'preparing', source: 'packet_builder' }
          : current)
      }, 500)
      const fallbackToAgent = (reason: string, packetStatus: string, packetKey?: string | null) => {
        if (cancelled) return
        const fallback = fallbackEnrichmentFromInstantCard(
          session,
          packetStatus === 'unsupported' ? 'packet unsupported' : 'packet unavailable',
          Math.max(0, Math.round(performance.now() - startedAt)),
        )
        if (fallback?.suggestedOptions.length) {
          window.clearTimeout(preparingHandle)
          window.clearTimeout(slowHandle)
          setEnrichment({
            ...fallback,
            packetKey: packetKey || fallback.packetKey || null,
            slowToolReason: reason,
          })
          return
        }
        setEnrichment((current) => current && current.cardKey === cardKey
          ? { ...current, status: 'error', source: 'client_card_fallback', cacheHit: false, packetKey: packetKey || current.packetKey || null, slowToolReason: reason }
          : current)
        enrichmentStartedAtRef.current = performance.now()
        void sendWithSessionContext(instantCardEnrichmentPrompt(session), 'instant_card_enrich', null, {
          enrichment_packet_hit: false,
          enrichment_packet_status: packetStatus || 'miss',
          enrichment_fallback_agent_used: true,
          enrichment_packet_key: packetKey || cardKey,
          enrichment_packet_build_latency_ms: Math.max(0, Math.round(performance.now() - startedAt)),
        })
      }
      const maybeSendContextInsight = (packet: CfoAiContextPacket | null, reason: string, cacheHit: boolean, latencyMs: number) => {
        if (cancelled || sentRef.current || !trimStr(session.seedPrompt)) return
        const usablePacket = packet || clientContextPacketFromCard(session, reason === 'context_packet_unsupported' ? 'unsupported' : 'warm_build')
        if (!usablePacket) return
        setContextPacket(usablePacket)
        sentRef.current = true
        const packetBytes = usablePacket.contextPacketBytes ?? measureBytes({
          cardKey: usablePacket.cardKey,
          status: usablePacket.status,
          changeExplanations: usablePacket.changeExplanations,
        })
        void sendWithSessionContext(session.seedPrompt as string, 'context_insight', null, {
          context_packet: usablePacket,
          context_packet_hit: cacheHit,
          context_packet_status: usablePacket.status,
          context_packet_lookup_ms: latencyMs,
          context_packet_build_ms: usablePacket.buildLatencyMs ?? (cacheHit ? 0 : latencyMs),
          context_packet_bytes: packetBytes,
          context_packet_key: usablePacket.packetKey || `${usablePacket.cardKey}:context`,
          used_cold_artifact: usablePacket.status === 'cold_slow',
          used_fallback_agent: false,
          used_broad_tool: false,
        })
      }
      const loadContextPacket = async () => {
        const wb = trimStr(workbookId)
        const contextStartedAt = performance.now()
        if (!wb || typeof (appApi as any).listQuickBooksCfoInstantCardContext !== 'function') {
          maybeSendContextInsight(null, 'context_packet_api_unavailable', false, Math.max(0, Math.round(performance.now() - contextStartedAt)))
          return
        }
        const query = enrichmentQueryForSession(session)
        try {
          const listed = await (appApi as any).listQuickBooksCfoInstantCardContext(wb, query)
          if (cancelled) return
          const listedPacket = mapInstantCardContextPacket(Array.isArray(listed?.packets) ? listed.packets[0] : null, true)
          if (listedPacket && (listedPacket.status === 'ready' || listedPacket.status === 'warm_build')) {
            maybeSendContextInsight(listedPacket, 'context_packet_hit', true, Math.max(0, Math.round(performance.now() - contextStartedAt)))
            return
          }
          if (typeof (appApi as any).ensureQuickBooksCfoInstantCardContext !== 'function') {
            maybeSendContextInsight(listedPacket, 'context_packet_ensure_unavailable', false, Math.max(0, Math.round(performance.now() - contextStartedAt)))
            return
          }
          const ensured = await (appApi as any).ensureQuickBooksCfoInstantCardContext(wb, { card: session.instantCard })
          if (cancelled) return
          const ensuredPacket = mapInstantCardContextPacket(ensured?.packet || ensured, Boolean(ensured?.cache_hit))
          maybeSendContextInsight(
            ensuredPacket,
            ensuredPacket?.status === 'unsupported' ? 'context_packet_unsupported' : 'context_packet_built',
            Boolean(ensured?.cache_hit),
            Math.max(0, Math.round(performance.now() - contextStartedAt)),
          )
        } catch {
          if (cancelled) return
          maybeSendContextInsight(null, 'context_packet_error', false, Math.max(0, Math.round(performance.now() - contextStartedAt)))
        }
      }
      void loadContextPacket()
      const handle = window.setTimeout(() => {
        const loadPacket = async () => {
          const wb = trimStr(workbookId)
          if (!wb || typeof (appApi as any).listQuickBooksCfoInstantCardEnrichments !== 'function') {
            fallbackToAgent('enrichment_packet_api_unavailable', 'not_ready', cardKey)
            return
          }
          const query = enrichmentQueryForSession(session)
          try {
            const listed = await (appApi as any).listQuickBooksCfoInstantCardEnrichments(wb, query)
            if (cancelled) return
            const listedPacket = mapInstantCardEnrichmentPacket(Array.isArray(listed?.packets) ? listed.packets[0] : null, true)
            if (listedPacket && listedPacket.status === 'ready') {
              onEnrichmentPacketReady?.(listedPacket)
              window.clearTimeout(preparingHandle)
              window.clearTimeout(slowHandle)
              setEnrichment({
                ...listedPacket,
                latencyMs: Math.max(0, Math.round(performance.now() - startedAt)),
                cacheHit: true,
                source: listedPacket.source || 'persisted_packet',
              })
              return
            }
            if (typeof (appApi as any).ensureQuickBooksCfoInstantCardEnrichment !== 'function') {
              fallbackToAgent('enrichment_packet_ensure_unavailable', listedPacket?.status || 'not_ready', listedPacket?.packetKey || cardKey)
              return
            }
            const ensured = await (appApi as any).ensureQuickBooksCfoInstantCardEnrichment(wb, { card: session.instantCard })
            if (cancelled) return
            const ensuredPacket = mapInstantCardEnrichmentPacket(ensured?.packet || ensured, Boolean(ensured?.cache_hit))
            if (ensuredPacket && ensuredPacket.status === 'ready') {
              onEnrichmentPacketReady?.(ensuredPacket)
              window.clearTimeout(preparingHandle)
              window.clearTimeout(slowHandle)
              setEnrichment({
                ...ensuredPacket,
                latencyMs: Math.max(0, Math.round(performance.now() - startedAt)),
                cacheHit: Boolean(ensured?.cache_hit),
                source: ensuredPacket.source || 'packet_builder',
              })
              return
            }
            fallbackToAgent(
              ensuredPacket?.status === 'unsupported' ? 'enrichment_packet_unsupported' : 'enrichment_packet_not_ready',
              ensuredPacket?.status || trimStr(ensured?.status) || 'not_ready',
              ensuredPacket?.packetKey || cardKey,
            )
          } catch (err) {
            if (cancelled) return
            window.clearTimeout(preparingHandle)
            window.clearTimeout(slowHandle)
            const fallback = fallbackEnrichmentFromInstantCard(
              session,
              'packet unavailable',
              Math.max(0, Math.round(performance.now() - startedAt)),
            )
            setEnrichment((current) => fallback || (current && current.cardKey === cardKey
              ? {
                  ...current,
                  status: 'error',
                  source: 'persisted_packet',
                  cacheHit: false,
                  slowToolReason: err instanceof Error ? err.message : 'enrichment_packet_error',
                }
              : current))
          }
        }
        void loadPacket()
      }, 120)
      return () => {
        cancelled = true
        window.clearTimeout(handle)
        window.clearTimeout(preparingHandle)
        window.clearTimeout(slowHandle)
      }
    }
    if (sentRef.current || !trimStr(session.seedPrompt)) return
    // For a ratio session, hold the seed until the full card resolves so the
    // grounding carries the component deltas (which input moved) — bounded by a
    // fallback so a slow fetch never hangs the answer. The base 60ms also lets
    // React StrictMode's dev double-mount settle (cleanup cancels the first).
    const isRatio = session.visual?.kind === 'ratio_health' || session.context.grounding?.source === 'ratio_card'
    const ready = !isRatio || !!embeddedRatio
    const handle = window.setTimeout(() => {
      if (sentRef.current) return
      sentRef.current = true
      void sendWithSessionContext(session.seedPrompt as string, session.mode === 'evidence' ? 'evidence_click' : 'continue')
    }, ready ? 60 : 1500)
    return () => window.clearTimeout(handle)
  }, [appApi, embeddedRatio, isRestored, onEnrichmentPacketReady, prefetchedEnrichment, sendWithSessionContext, session, workbookId])

  useEffect(() => {
    if (isRestored || !session.instantCard || !live.response) return
    const current = enrichment
    if (!current || (current.status !== 'loading' && current.status !== 'slow')) return
    if (current.cardKey !== session.instantCard.cardKey) return
    const latencyMs = enrichmentStartedAtRef.current ? Math.max(0, Math.round(performance.now() - enrichmentStartedAtRef.current)) : null
    const toolNames = Array.isArray(live.response.tool_calls)
      ? live.response.tool_calls.map((call) => trimStr(call.name)).filter(Boolean)
      : []
    enrichmentAgentRunIdRef.current = trimStr(live.response.agent_run_id)
    setEnrichment({
      cardKey: current.cardKey,
      freshnessToken: current.freshnessToken ?? null,
      status: 'ready',
      suggestedOptions: optionsFromEnrichmentResponse(live.response, session),
      summaryFacts: factsFromEnrichmentResponse(live.response, latencyMs),
      latencyMs,
      source: 'fast_lane_tools',
      toolNames,
      cacheHit: toolNames.length === 0,
      slowToolReason: latencyMs != null && latencyMs > ENRICHMENT_SLOW_MS ? 'fast_lane_enrichment_exceeded_1500ms' : null,
    })
  }, [enrichment, isRestored, live.response, session])

  useEffect(() => {
    if (!error || !session.instantCard || !enrichment || (enrichment.status !== 'loading' && enrichment.status !== 'slow')) return
    setEnrichment((current) =>
      current && current.cardKey === session.instantCard?.cardKey
        ? { ...current, status: 'error', slowToolReason: trimStr(error) || current.slowToolReason || null }
        : current,
    )
  }, [enrichment, error, session.instantCard])

  // Resolve the full RatioCard for the embedded detail card. The Ratios page
  // passes it directly; from Home we lazily fetch (the instant gauge covers the
  // gap so there's never a blank wait). Ratio sessions only.
  useEffect(() => {
    const fromSelection = (session.context.selection as Record<string, any> | null)?.ratio_card as RatioCard | undefined
    if (fromSelection && trimStr(fromSelection.id)) {
      setEmbeddedRatio(fromSelection)
      return
    }
    const isRatioSession =
      session.visual?.kind === 'ratio_health' || session.context.grounding?.source === 'ratio_card'
    const metricId = trimStr(session.context.metricId)
    const wb = trimStr(workbookId)
    if (!isRatioSession || !metricId || !wb || typeof appApi?.getQuickBooksRatios !== 'function') {
      setEmbeddedRatio(null)
      return
    }
    let cancelled = false
    setEmbeddedRatio(null)
    getCachedQuickBooksRatios(appApi, wb, session.context.period)
      .then((resp) => {
        if (cancelled) return
        const found =
          (resp?.categories || []).flatMap((group) => group.ratios || []).find((r) => r.id === metricId) || null
        setEmbeddedRatio(found)
      })
      .catch(() => {
        if (!cancelled) setEmbeddedRatio(null)
      })
    return () => {
      cancelled = true
    }
  }, [appApi, workbookId, session.context, session.visual])

  // Persist the session (transcript + answer) once a live turn settles, so
  // History can restore exactly what was generated.
  useEffect(() => {
    if (isRestored || live.busy || !live.response) return
    const runId = trimStr(live.response.agent_run_id)
    if (enrichmentAgentRunIdRef.current && runId === enrichmentAgentRunIdRef.current) return
    const sig = `${runId}:${live.messages.length}`
    if (persistSigRef.current === sig) return
    persistSigRef.current = sig
    const visibleTranscript = visibleMessagesForSession(session, live.messages)
    const drilldownMetadata = firstDrilldownMetadata(visibleTranscript)
    const firstUser = visibleTranscript.find((m) => m.role === 'user')
    const label =
      session.mode === 'ask'
        ? trimStr(firstUser?.content).slice(0, 90) || trimStr(live.response.answer?.headline) || 'Ask the analyst'
        : trimStr(session.context.metricLabel) || session.label
    onPersistSession({
      id: session.id,
      label,
      mode: session.mode,
      manifest_version: drilldownMetadata.manifestVersion || CFO_DRILLDOWN_MANIFEST.version,
      recipe_id: drilldownMetadata.recipeId,
      period: session.monthKey,
      context: session.context,
      visual: session.visual,
      instantCard: displayCard,
      transcript: visibleTranscript,
      answer: hideFinalAnswer ? null : live.response.answer ?? null,
      visuals: Array.isArray(live.response.visuals) ? live.response.visuals : null,
      openedAt: session.openedAt,
    })
	  }, [displayCard, hideFinalAnswer, isRestored, live.busy, live.response, live.messages, onPersistSession, session])

  // Esc to close + focus management (focus trap-lite: focus the dialog on open,
  // restore focus to the previously focused element on close).
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    const node = dialogRef.current
    node?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      previous?.focus?.()
    }
  }, [onClose])

  const headerChips = useMemo(() => {
    const c = session.context
    // The metric label is already the modal title — repeating it as a chip (and
    // rendering the raw comparison kind, e.g. "mom" → "Mom") read as clutter.
    return [c.asOfLabel, comparisonShortLabel(c.comparison), c.pageId]
      .map((v) => trimStr(v))
      .filter(Boolean)
  }, [session.context])

  const intro = isRestored
    ? restoredUsesOlderManifest
      ? 'Saved analysis from an older drilldown manifest. Continue it below to rerun with the current recipe.'
      : openEndedAskEnabled
        ? 'Saved analysis — exactly what you generated before. Continue it below to ask more.'
        : 'Saved analysis — exactly what you generated before. Continue it below to rerun it against current data.'
    : session.mode === 'ask'
      ? 'I’ve read this workbook’s books. Ask me anything about this page — I’ll show the evidence first, then explain it.'
      : null

  // Chart/chip clicks: drive the live agent, or (in a restored view) spin up a
  // fresh live continuation seeded with that next step.
  const handlePrompt = (p: string) => {
    if (isRestored) onContinue(session, p)
    else void sendWithSessionContext(p)
  }

  const handleCopyTranscript = async () => {
    const ok = await copyHtmlToClipboard(buildCfoAnalysisSessionHtml({ session, visibleMessages, response: streamResponse }))
    setCopyState(ok ? 'copied' : 'failed')
    window.setTimeout(() => setCopyState('idle'), 1800)
  }

  const handleTag = async () => {
    if (tagBusy || tagged) return
    setTagBusy(true)
    const answer = response?.answer
    const narrative = [trimStr(answer?.executive_summary), ...((answer?.detailed_narrative || []).map(trimStr))]
      .filter(Boolean)
      .join('\n\n')
    try {
      await onTagToReport({
        session_id: session.id,
        agent_run_id: trimStr(response?.agent_run_id) || null,
        page_id: session.context.pageId,
        metric_id: session.context.metricId || null,
        metric_label: session.context.metricLabel || null,
        period: session.monthKey,
        title: trimStr(answer?.headline) || session.label,
        narrative: narrative || null,
        visual: session.visual,
        citations: Array.isArray(answer?.evidence_labels) ? answer?.evidence_labels : null,
        visuals: Array.isArray(response?.visuals) ? response?.visuals : null,
      })
      setTagged(true)
    } catch {
      // surfaced via report panel; keep the button actionable
      setTagged(false)
    } finally {
      setTagBusy(false)
    }
  }

  const answerFooter = streamResponse?.answer ? (
    <div className="cfo-analysis-modal-answerbar">
      {!isRestored && streamResponse.agent_run_id ? (
        <>
          <AiFeedbackControl aiTraceId={streamResponse.agent_run_id} surface={surface} workbookId={workbookId} />
          <ReportProblemButton surface={surface} aiTraceId={streamResponse.agent_run_id} />
        </>
      ) : null}
      <button type="button" className="cfo-analysis-modal-tag" onClick={() => void handleTag()} disabled={tagBusy || tagged}>
        {tagged ? (
          <>
            <Check size={13} /> Tagged to {session.monthKey} report
          </>
        ) : (
          <>
            <FileText size={13} /> Tag to this month’s report
          </>
        )}
      </button>
    </div>
  ) : null

  return (
    <div className="cfo-analysis-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div
        className="cfo-analysis-modal"
        role="dialog"
        aria-modal="true"
        aria-label="CFO analysis"
        tabIndex={-1}
        ref={dialogRef}
      >
        <header className="cfo-analysis-modal-head">
          <div className="cfo-analysis-modal-orb" aria-hidden="true">
            <Sparkles size={16} />
          </div>
          <div className="cfo-analysis-modal-title">
            <b>
              {isRestored
                ? `Saved · ${session.context.metricLabel || session.label}`
                : session.mode === 'ask'
                  ? 'Ask the analyst'
                  : session.context.metricLabel || 'Guided analysis'}
            </b>
            <div className="cfo-analysis-modal-chips">
              {headerChips.map((chip, idx) => (
                <span className="cfo-analysis-modal-chip" key={`${chip}-${idx}`}>
                  {chip}
                </span>
              ))}
            </div>
          </div>
          <div className="cfo-analysis-modal-actions">
            <button
              type="button"
              className="cfo-analysis-modal-iconbtn"
              aria-label="Copy session HTML"
              title="Copy session HTML"
              onClick={() => void handleCopyTranscript()}
            >
              {copyState === 'copied' ? <Check size={16} /> : <Clipboard size={16} />}
            </button>
            {openEndedAskEnabled ? (
              <button
                type="button"
                className="cfo-analysis-modal-iconbtn"
                aria-label="Analysis history"
                aria-expanded={historyOpen}
                onClick={() => setHistoryOpen((v) => !v)}
              >
                <History size={16} />
              </button>
            ) : null}
            <button
              type="button"
              className="cfo-analysis-modal-iconbtn"
              aria-label="Open this month’s report"
              onClick={() => onOpenReport(session.monthKey)}
            >
              <FileText size={16} />
            </button>
            <button type="button" className="cfo-analysis-modal-iconbtn" aria-label="Close" onClick={onClose}>
              <X size={16} />
            </button>
          </div>
          {copyState !== 'idle' ? (
            <div className={`cfo-analysis-modal-copytoast ${copyState}`}>
              {copyState === 'copied' ? 'Copied HTML' : 'Copy failed'}
            </div>
          ) : null}
        </header>

        {historyOpen && openEndedAskEnabled ? (
          <div className="cfo-analysis-modal-history">
            <div className="cfo-analysis-modal-history-tools">
              <div className="cfo-analysis-modal-search">
                <Search size={14} aria-hidden="true" />
                <input
                  type="search"
                  value={historyQuery}
                  placeholder="Search saved analysis…"
                  aria-label="Search analysis history"
                  onChange={(e) => onHistoryQueryChange(e.target.value)}
                />
              </div>
              <button
                type="button"
                className={`cfo-analysis-modal-archtoggle${historyArchived ? ' is-on' : ''}`}
                aria-pressed={historyArchived}
                onClick={() => onToggleHistoryArchived(!historyArchived)}
              >
                <Archive size={13} /> {historyArchived ? 'Archived' : 'Active'}
              </button>
            </div>
            {history.length === 0 ? (
              <p className="cfo-analysis-modal-history-empty">
                {historyQuery.trim()
                  ? 'No saved analysis matches your search.'
                  : historyArchived
                    ? 'No archived analysis yet.'
                    : 'No saved analysis yet. Run an analysis and it’ll appear here to revisit.'}
              </p>
            ) : (
              history.map((h) => (
                <div key={h.id} className="cfo-analysis-modal-history-item">
                  <button
                    type="button"
                    className="cfo-analysis-modal-history-open"
                    onClick={() => {
                      setHistoryOpen(false)
                      onReopen(h)
                    }}
                  >
                    <span>{h.label}</span>
                    <small>{h.mode}{h.period ? ` · ${h.period}` : ''}</small>
                  </button>
                  <div className="cfo-analysis-modal-history-actions">
                    <button
                      type="button"
                      className="cfo-analysis-modal-history-act"
                      aria-label={historyArchived ? 'Unarchive' : 'Archive'}
                      title={historyArchived ? 'Unarchive' : 'Archive'}
                      onClick={() => onArchiveSession(h.id, !historyArchived)}
                    >
                      {historyArchived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                    </button>
                    <button
                      type="button"
                      className="cfo-analysis-modal-history-act danger"
                      aria-label="Delete"
                      title="Delete permanently"
                      onClick={() => onDeleteSession(h.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : null}

        <div className="cfo-analysis-modal-body">
          {/* Re-establish the CFO design-token + component-style scope: the modal
              renders outside the page's .cfo-shell, so token-based card colors
              keep their styling. */}
          <div className="cfo-analysis-modal-scope cfo-shell">
            {displayCard ? (
              <>
                <CfoInstantCardView
                  card={displayCard}
                  onPrompt={handlePrompt}
                  onAction={handleCardAction}
                  extraActions={enrichment?.status === 'ready' ? enrichment.suggestedOptions : undefined}
                  disabled={tagBusy}
                  driverControls={nextLevelControls}
                />
              </>
            ) : null}

            {exploreLinks.length ? (
              <nav className="cfo-analysis-modal-navlinks" aria-label="Explore further">
                <span className="cfo-analysis-modal-navlinks-label">Explore further</span>
                {exploreLinks.map((link) => (
                  <button
                    key={link.page}
                    type="button"
                    className="cfo-analysis-modal-navlink"
                    onClick={() => handleExploreLink(link.page)}
                  >
                    {link.label} <ArrowUpRight size={13} aria-hidden="true" />
                  </button>
                ))}
              </nav>
            ) : null}

            {shouldShowSeedEvidence && session.visual ? (
              <div className="cfo-analysis-modal-evidence">
                {session.visual.kind === 'ratio_health' ? (
                  embeddedRatio ? (
                    <div className="cfo-analysis-modal-ratiodetail">
                      <RatioDetailCard ratio={embeddedRatio} ratioWindow={embeddedRatioWindow} />
                    </div>
                  ) : (
                    <div className="cfo-analysis-modal-ratioskeleton" aria-busy="true">
                      Loading the {session.visual.label} ratio card…
                    </div>
                  )
                ) : (
                  <>
                    <div className="cfo-analysis-modal-evidence-tag">
                      <Sparkles size={12} /> Your evidence
                    </div>
                    {session.visual.kind === 'chart' ? (
                      <>
                        {session.visual.caption ? <p className="cfo-analysis-modal-evidence-cap">{session.visual.caption}</p> : null}
                        <CfoAnalysisChart chart={session.visual.chart} onPrompt={handlePrompt} disabled={busy} />
                      </>
                    ) : (
                      <div className="cfo-analysis-modal-valuecard">
                        <div className="lab">{session.visual.label}</div>
                        <div className="val">{session.visual.display}</div>
                        {session.visual.sub ? <div className="sub">{session.visual.sub}</div> : null}
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : null}

            <CfoAnalysisStream
              messages={visibleMessages}
              response={streamResponse}
              busy={busy && !isEnrichmentTurn}
              error={error}
              promptChips={promptChips}
              onPrompt={handlePrompt}
              intro={intro}
              answerFooter={answerFooter}
            />
          </div>
        </div>

        {isRestored ? (
          <div className="cfo-analysis-modal-restorebar">
            <span>
              {restoredUsesOlderManifest
                ? `Saved analysis used ${restoredManifestVersion}; current manifest is ${CFO_DRILLDOWN_MANIFEST.version}.`
                : `You’re viewing a saved analysis${restoredRecipeId ? ` · ${restoredRecipeId}` : ''}.`}
            </span>
            <button type="button" className="cfo-analysis-modal-continue" onClick={() => onContinue(session)}>
              Continue this analysis →
            </button>
          </div>
        ) : !openEndedAskEnabled ? null : (
          <form
            className="cfo-analysis-modal-composer"
            onSubmit={(e) => {
              e.preventDefault()
              void sendWithSessionContext(input)
            }}
          >
            <textarea
              ref={composerRef}
              className="cfo-analysis-modal-input"
              value={input}
              rows={1}
              placeholder="Ask a follow-up, or pick a next step above…"
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  void sendWithSessionContext(input)
                }
              }}
            />
            <button type="submit" className="cfo-analysis-modal-send" disabled={busy || !input.trim()} aria-label="Send">
              ↑
            </button>
          </form>
        )}
        <div className="cfo-analysis-modal-consent">🔒 Your financial data stays in your workbook · answers are cited</div>
      </div>
    </div>
  )
}

export default CfoAnalysisModal
